import { Link, Navigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { FEATURES, featureBySlug, registerHref } from "./catalog";
import { DEMOS } from "./scenes";

const fade = {
  hidden: { opacity: 0, y: 24 },
  show: { opacity: 1, y: 0, transition: { duration: 0.55, ease: [0.22, 1, 0.36, 1] } },
};

function AuthBar({ feature }) {
  return (
    <div className="flex flex-wrap gap-3" data-testid="feature-auth">
      <Link
        to={registerHref(feature.registerRole)}
        className="px-5 py-2.5 rounded-full btn-accent text-sm font-semibold"
        data-testid="feature-signup"
      >
        Sign up
      </Link>
      <Link
        to="/auth?mode=login"
        className="px-5 py-2.5 rounded-full bg-white text-black text-sm font-semibold"
        data-testid="feature-login"
      >
        Login
      </Link>
      <Link
        to={feature.openTo}
        className="px-5 py-2.5 rounded-full border border-white/20 text-sm font-semibold inline-flex items-center gap-2 hover:border-aura-cyan"
        data-testid="feature-open"
      >
        {feature.openLabel} <ArrowRight className="w-4 h-4" />
      </Link>
    </div>
  );
}

export default function FeaturePage() {
  const { slug } = useParams();
  const feature = featureBySlug(slug);
  if (!feature) return <Navigate to="/features/all-capabilities" replace />;
  const Demo = DEMOS[feature.slug] || CompareDemo;
  const Icon = feature.icon;
  const others = FEATURES.filter((item) => item.slug !== feature.slug);

  return (
    <div data-testid={`feature-page-${feature.slug}`}>
      <section className="relative overflow-hidden border-b border-aura-border">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_15%_20%,rgba(247,37,133,0.16),transparent_42%)]" />
        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 py-16 lg:py-20 grid lg:grid-cols-2 gap-12 items-center">
          <motion.div initial="hidden" animate="show" variants={fade}>
            <div className="flex items-center gap-2 text-[11px] tracking-luxe uppercase text-aura-muted mb-4">
              <Icon className="w-4 h-4 text-aura-accent" strokeWidth={1.5} />
              {feature.kicker}
            </div>
            <h1 className="font-serif text-4xl lg:text-5xl tracking-tight mb-4">{feature.headline}</h1>
            <p className="text-aura-muted leading-relaxed max-w-xl mb-8">{feature.lede}</p>
            <AuthBar feature={feature} />
          </motion.div>
          <Demo />
        </div>
      </section>

      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16">
        <div className="grid md:grid-cols-3 gap-px bg-aura-border border border-aura-border">
          {feature.points.map((point, i) => (
            <motion.div
              key={point}
              initial={{ opacity: 1, y: 0 }}
              animate={{ y: [12, 0] }}
              transition={{ delay: i * 0.08, duration: 0.4 }}
              className="bg-aura-bg p-6 text-sm leading-relaxed"
            >
              {point}
            </motion.div>
          ))}
        </div>
        <div className="mt-10">
          <AuthBar feature={feature} />
        </div>
      </section>

      <section className="border-t border-aura-border">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-14">
          <div className="text-[11px] tracking-luxe uppercase text-aura-muted mb-4">More features</div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {others.map((item) => (
              <Link
                key={item.slug}
                to={`/features/${item.slug}`}
                className="border border-aura-border px-4 py-3 hover:border-aura-accent transition-colors"
              >
                <div className="text-sm font-medium">{item.title}</div>
                <div className="text-[11px] text-aura-muted mt-0.5">{item.blurb}</div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
